import { FC } from 'react';
import style from './CoatingStep4Style.module.css';
import { ReactComponent as ExclamationmarkCircle } from '@/assets/images/icon-exclamationmark_circle.svg';
import { Button } from '@/components/uiParts/Button';
import { CoatingStep1FormData } from '@/hooks/api/coating/useCoatingStep1';
import { CoatingStep2FormData } from '@/hooks/api/coating/useCoatingStep2';
import { CoatingStep3FormData } from '@/hooks/api/coating/useCoatingStep3';
import { useCoatingStep4 } from '@/hooks/api/coating/useCoatingStep4';
import {
  CoatingCourseItem,
  CoatingOptionItem,
  CreateCoatingParams,
} from '@/types/api/coatingApi';

interface Props {
  step1Data: CoatingStep1FormData;
  step2Data: CoatingStep2FormData;
  step3Data: CoatingStep3FormData;
  courseItems: CoatingCourseItem[];
  optionItems: CoatingOptionItem[];
  totalFee: number;
  onSubmit: (data?: CreateCoatingParams) => void;
  submitLabel?: string;
  onClickCancel: () => void;
}

export const CoatingStep4: FC<Props> = ({
  step1Data,
  step2Data,
  step3Data,
  courseItems,
  optionItems,
  totalFee,
  onSubmit,
  submitLabel,
  onClickCancel,
}) => {
  const { data, display } = useCoatingStep4(
    step1Data,
    step2Data,
    step3Data,
    courseItems,
    optionItems,
    totalFee,
  );

  const handleSubmit = () => {
    onSubmit(data);
  };

  return (
    <div className={style['container']}>
      <h3 className={style['heading']}>予約内容</h3>
      <p className={style['label-text']}>予約内容をご確認ください。</p>

      {/* コーティング情報 */}
      <div className={`${style['list']} ${style['no-last-border']}`}>
        {display.courseName && (
          <div className={style['list-item']}>
            <div className={style['list-item-label']}>コース</div>
            <div className={style['list-item-value']}>{display.courseName}</div>
          </div>
        )}
        {display.bodySizeLabel && (
          <div className={style['list-item']}>
            <div className={style['list-item-label']}>車体サイズ</div>
            <div className={style['list-item-value']}>
              {display.bodySizeLabel}
            </div>
          </div>
        )}
        {display.polishOptionLabel && (
          <div className={style['list-item']}>
            <div className={style['list-item-label']}>磨きオプション</div>
            <div className={style['list-item-value']}>
              {display.polishOptionLabel}
            </div>
          </div>
        )}
        <div className={style['list-item']}>
          <div className={style['list-item-label']}>サービス内容</div>
          <div className={style['list-item-value']}>
            {display.serviceContents}
          </div>
        </div>
        <div className={style['list-item']}>
          <div className={style['list-item-label']}>ご利用金額</div>
          <div className={style['list-item-value']}>￥{display.amount}</div>
        </div>
      </div>

      {/* 希望日時 */}
      <div className={style['lsit']}>
        <p className={style['list-title']}>ご予約希望日時</p>
        <div className={style['list-item']}>
          <div className={style['list-item-label']}>第一希望</div>
          <div className={style['list-item-value']}>{display.datetime1}</div>
        </div>
        <div className={style['list-item']}>
          <div className={style['list-item-label']}>第二希望</div>
          <div className={style['list-item-value']}>{display.datetime2}</div>
        </div>
        <div className={style['list-item']}>
          <div className={style['list-item-label']}>第三希望</div>
          <div className={style['list-item-value']}>{display.datetime3}</div>
        </div>
      </div>

      {/* 予約者情報 */}
      <div className={style['lsit']}>
        <p className={style['list-title']}>お客様の情報</p>
        <div className={style['list-item']}>
          <div className={style['list-item-label']}>お名前</div>
          <div className={style['list-item-value']}>{display.name}</div>
        </div>
        <div className={style['list-item']}>
          <div className={style['list-item-label']}>フリガナ</div>
          <div className={style['list-item-value']}>{display.nameKana}</div>
        </div>
        <div className={style['list-item']}>
          <div className={style['list-item-label']}>お電話番号</div>
          <div className={style['list-item-value']}>{display.phoneNo}</div>
        </div>
      </div>

      {/* 車両情報 */}
      <div className={style['lsit']}>
        <p className={style['list-title']}>お車の情報</p>
        <div className={style['list-item']}>
          <div className={style['list-item-label']}>車両ナンバー</div>
          <div className={style['list-item-value']}>{display.carLabel}</div>
        </div>
        <div className={style['list-item']}>
          <div className={style['list-item-label']}>メーカー</div>
          <div className={style['list-item-value']}>{display.makerName}</div>
        </div>
        <div className={style['list-item']}>
          <div className={style['list-item-label']}>車種</div>
          <div className={style['list-item-value']}>{display.carName}</div>
        </div>
        {display.bodyColor && (
          <div className={style['list-item']}>
            <div className={style['list-item-label']}>ボディカラー</div>
            <div className={style['list-item-value']}>{display.bodyColor}</div>
          </div>
        )}
        {display.pastCoatingHistory && (
          <div className={style['list-item']}>
            <div className={style['list-item-label']}>過去コーティング歴</div>
            <div className={style['list-item-value']}>
              {display.pastCoatingHistory}
            </div>
          </div>
        )}
      </div>

      {/* 備考 */}
      {display.unitMemo && (
        <div className={style['lsit']}>
          <p className={style['list-title']}>備考</p>
          <div className={style['list-item']}>
            <div className={style['list-item-value']}>{display.unitMemo}</div>
          </div>
        </div>
      )}

      {/* 決済情報 */}
      <div className={style['payment-info']}>
        <p className={style['payment-info-title']}>お支払い金額</p>
        <p className={style['payment-info-amount']}>
          ￥{display.amount}
          <span style={{ fontSize: '14px', fontWeight: 400 }}>（税込）</span>
        </p>
        <p style={{ marginTop: '8px', color: '#707070' }}>
          クレジットカードでのお支払いとなります。「決済に進む」ボタンを押すと決済画面に遷移します。
        </p>
      </div>

      {/* 注意事項 */}
      <div className={style['warning-box']}>
        <div className={style['warning-box-title']}>
          <ExclamationmarkCircle />
          <span>ご注意事項</span>
        </div>
        <ul className={style['warning-box-list']}>
          <li>
            コーティング施工にはお預かり期間が必要です。コースにより日数が異なります。
          </li>
          <li>
            ご予約後のキャンセルには、キャンセル料が発生する場合があります。
          </li>
          <li>悪天候等により施工日が変更になる場合がございます。</li>
          <li>
            <span className={style['text-alert']}>
              予約の変更・キャンセルの受付期間は予約日より３日前までです。それ以降はお電話にてお問い合わせください。
            </span>
          </li>
        </ul>
      </div>

      <div className={style['button-group']}>
        <Button text={submitLabel || '決済に進む'} onClick={handleSubmit} />
        <Button
          style={{
            color: '#232323',
            backgroundColor: '#fff',
            marginTop: '8px',
          }}
          text="キャンセル"
          onClick={onClickCancel}
        />
      </div>
    </div>
  );
};
